import { createSelector } from '@ngrx/store';

import { cartDataSelector } from '@fcom/booking/store/selectors';

import {
  CartLineItem,
  toCommittedLineItems,
  toLocalCartLineItems,
  withCommittedSeatNames,
} from '../../utils/cart-line-items.utils';
import { diffLineItems, SelectionDelta } from '../../utils/cart-selection-diff.utils';
import { serviceSelections } from './services.selector';
import { seatMapNonBundledSeatSelectionsByFlightSelector } from './seat-map.selector';

/**
 * The complete local desired selection as canonical, de-duplicated line items: standalone products, seat
 * bundles, ancillary+ancillary combos and plain seats, each once, with seat/seat-bundle precedence applied.
 * This is the single source the shopping cart renders its product list from (see
 * `ShoppingCartBoundComponent.productGroups`) — no `dirty` flag, no committed/pending two-list stitch. When the
 * cart is clean, local mirrors committed, so this is also what is currently committed.
 */
export const localCartLineItemsSelector = createSelector(
  cartDataSelector,
  serviceSelections,
  seatMapNonBundledSeatSelectionsByFlightSelector,
  (cart, selections, seatSelectionsByFlight): CartLineItem[] =>
    withCommittedSeatNames(
      toLocalCartLineItems(selections, seatSelectionsByFlight),
      toCommittedLineItems(cart?.services?.unpaid)
    )
);

/**
 * The difference between the local desired selection and the committed cart, as a quantity-aware delta of
 * added/removed line items. This is the single source the shopping cart drives totals off: the committed
 * cart total stays authoritative and this delta is applied on top (see `applyDelta`), so the preview total
 * is correct across combo displacement, per-pax splits and in-flight cart updates without any `dirty` flag
 * or page-scoped hidden-category bookkeeping.
 *
 * When the local state matches the committed cart (the clean case), the diff is empty and the total
 * collapses back to the committed total.
 */
export const selectionDeltaSelector = createSelector(
  cartDataSelector,
  serviceSelections,
  seatMapNonBundledSeatSelectionsByFlightSelector,
  (cart, selections, seatSelectionsByFlight): SelectionDelta =>
    diffLineItems(
      toCommittedLineItems(cart?.services?.unpaid),
      toLocalCartLineItems(selections, seatSelectionsByFlight)
    )
);
